import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Context } from '../../store/Store'
import { planificationApi } from '../../services/planificationApi'
import { mapProducteurToApiCode } from '../../utils/planificationMapping'

/**
 * Dashboard de planification
 * Affiche un tableau des planifications existantes du producteur
 */
export function PlanificationDashboard() {
  const { app } = React.useContext(Context)
  const [planifications, setPlanifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    const user = JSON.parse(window.localStorage.getItem('user') || '{}')
    const producteurCode = mapProducteurToApiCode(user.nicename)

    if (!producteurCode) {
      setLoading(false)
      return
    }

    const fetchData = async () => {
      try {
        const records = await planificationApi.listByProducteur(producteurCode)
        if (!cancelled) {
          setPlanifications(records)
        }
      } catch (err) {
        if (!cancelled) {
          setError('Erreur lors du chargement des planifications')
          console.error(err)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchData()
    return () => { cancelled = true }
  }, [app.profil])

  if (loading) {
    return (
      <div className="m-6 text-center">
        <p>Chargement...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="m-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="m-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Mes Planifications</h2>
        <Link
          to="new"
          className="bg-base text-white px-4 py-2 rounded-md hover:bg-base-dark"
        >
          + Ajouter une ligne
        </Link>
      </div>

      {planifications.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          Aucune planification pour le moment.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Légume
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Surface (Ha)
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Certificat
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantités
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {planifications.map((planif, index) => {
                // Calcul du total des quantités
                const totalQty = Object.values(planif.quantites || {}).reduce(
                  (sum, q) => sum + (typeof q === 'object' ? q.quantite : q),
                  0
                )
                const legumeLabel = planif.legumeId.replace('|', ' ')

                return (
                  <tr key={planif.legumeId || index} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="font-medium">{legumeLabel}</span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {planif.surface ?? '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {planif.certificat || '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {totalQty} {planif.unite || 'KG'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-right">
                      <Link
                        to={`edit/${encodeURIComponent(planif.legumeId)}`}
                        className="text-base hover:text-base-dark mr-3"
                      >
                        Modifier
                      </Link>
                      <button
                        onClick={() => handleDelete(planif.legumeId)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )

  async function handleDelete(legumeId) {
    if (!window.confirm('Voulez-vous vraiment supprimer cette planification ?')) {
      return
    }

    const user = JSON.parse(window.localStorage.getItem('user') || '{}')
    const producteurCode = mapProducteurToApiCode(user.nicename)
    try {
      await planificationApi.remove({ producteurId: producteurCode, legumeId })
      setPlanifications((prev) => prev.filter((p) => p.legumeId !== legumeId))
    } catch (err) {
      console.error('Erreur suppression', err)
      alert('Erreur lors de la suppression')
    }
  }
}
